import { HStack } from "@chakra-ui/layout";
import {
  Box,
  Button,
  Divider,
  Stack,
  Text,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import React, { useEffect, useState } from "react";
import { ApiClient, ApiResult } from "../../api/apiClient";
import { CherryPayApi } from "../../api/models";
import { TextField } from "../../components/fields/TextField/TextField";
import { DateField } from "../../components/DateField/DateField";
import { TimeField } from "../../components/TimeField/TimeField";
import { Card } from "../../components/Card/Card";
import { isEmptyStr } from "../../util/StringUtil";
import { MemberFilterQueryModal } from "../../components/MemberFilterQueryModal/MemberFilterQueryModal";
import { FieldWrapper } from "../../components/fields/FieldWrapper/FieldWrapper";
import { Textarea } from "@chakra-ui/textarea";
import { ApiEnumConfigurations } from "../../enums/ApiEnumConfigurations";
import { useApiRequest } from "../../hooks/useApiRequest";
import { SingleValue } from "chakra-react-select";
import {
  ChakraReactSelectField,
  SelectOption,
} from "../../components/fields/SelectField/ChakraReactSelectField";
import { EventPreview } from "../../components/EventPreview/EventPreview";
import {
  useCanPublishEvent,
  useGetSaveButtonLabel,
  useGetSaveButtonTheme,
} from "./EventFormUtil";
import { PublishEventIcon } from "../../styles/icons";

interface EventFormProps {
  apiClient: ApiClient;
  businessId: string;
  event?: CherryPayApi.Event | null;
  eventName: string | null;
  onCancel: () => void;
  onSuccess: (event: CherryPayApi.Event) => void;
  onFailure: (message?: string) => void;
}

export interface EventFormValues {
  eventKey?: string | null;
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  checkInStartTime: string;
  checkInEndTime: string;
  eventLocation: string;
  eventTimezone: string | null;
  memberFilterQuery: string;
  isPublishRequested?: boolean; // Track if publish was requested
}

const InnerForm = ({
  isSubmitting,
  isValid,
  values,
  dirty,
  businessId,
  event,
  eventName,
  onCancel,
  onSuccess,
  onFailure,
  submitForm,
  setFieldValue,
  apiClient,
}: EventFormProps & FormikProps<EventFormValues>) => {
  const [filterQuery, setFilterQuery] = useState<string>(
    values.memberFilterQuery
  );
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const memberFilterQueryModal = useDisclosure();
  const { data: timezones, isLoading } = useApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(
        businessId,
        ApiEnumConfigurations.Timezone
      ),
    []
  );

  // Use the memoized hooks
  const saveButtonTheme = useGetSaveButtonTheme(event);
  const saveButtonLabel = useGetSaveButtonLabel(event);
  const canPublish = useCanPublishEvent(event);

  // Fetch member count when filter query changes
  useEffect(() => {
    if (businessId) {
      setMemberCount(null);
      apiClient
        .getEligibleMemberCount(businessId, values.memberFilterQuery)
        .then((result) => {
          if (result.ok) {
            setMemberCount(result.data.Count ?? 0);
          } else {
            setMemberCount(null);
            console.error("Failed to fetch member count:", result.message);
          }
        });
    }
  }, [apiClient, businessId, values.memberFilterQuery]);

  const onSuccessMemberFilterQuery = (query: string | undefined) => {
    if (query !== undefined) {
      setFilterQuery(query);
      setFieldValue("memberFilterQuery", query);
    }
    memberFilterQueryModal.onClose();
  };

  const handlePublishClick = () => {
    setFieldValue("isPublishRequested", true);
    submitForm();
  };

  return (
    <Card w="100%">
      <Stack
        w="100%"
        alignItems="start"
        spacing={{ base: 4, xl: 24 }}
        bgColor="gray.600"
        py={5}
        px={8}
        borderTopStartRadius="3xl"
        borderTopEndRadius="3xl"
        direction={{ base: "column", xl: "row" }}
      >
        <VStack spacing="1" alignItems="flex-start">
          <Text fontSize="2xl" color="white" fontWeight="bold">
            {event != null ? eventName : "New Event"}
          </Text>
        </VStack>

        <HStack
          flex="1"
          justifyContent="end"
          position="relative"
          zIndex={100}
          alignSelf="center"
        >
          <Box>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </Box>

          <Box>
            <Button
              colorScheme={saveButtonTheme}
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={submitForm}
            >
              {saveButtonLabel}
            </Button>
          </Box>

          {canPublish && (
            <Box>
              <Button
                colorScheme={"cherryButton"}
                type="button"
                isLoading={isSubmitting}
                disabled={isSubmitting || !isValid}
                onClick={handlePublishClick}
                leftIcon={<PublishEventIcon />}
              >
                Publish Event
              </Button>
            </Box>
          )}
        </HStack>
      </Stack>

      <VStack
        w="100%"
        p={8}
        color="cherryGrey.900"
        bgColor="white"
        borderBottomLeftRadius="3xl"
        borderBottomRightRadius="3xl"
        boxShadow="md"
        spacing={6}
        align="start"
      >
        <Stack w="100%" direction={["column", "row"]} spacing={6} align="start">
          {/* Left side - Form */}
          <Box
            w={["100%", "50%"]}
            p={4}
            border="1px solid"
            borderColor="cherryButtonDark.50"
            borderRadius="md"
          >
            <HStack width="100%" mb={2}>
              <Text fontSize="sm" flex="1">
                <b>Event Details</b> :
              </Text>
            </HStack>
            <Divider mb={2} />
            <VStack w="100%" alignItems="flex-start" spacing={6} pb={10}>
              <TextField
                name="eventName"
                label="Event Name"
                placeholder="Event Name"
                isRequired
              />

              <Wrap w="100%" spacing={5}>
                <WrapItem flex="1" maxWidth="180px">
                  <DateField
                    name="eventStartDate"
                    label="Event Start Date"
                    placeholder="Start Date"
                    isRequired
                    dateOnly={true}
                  />
                </WrapItem>
                <WrapItem flex="1" maxWidth="180px">
                  <DateField
                    name="eventEndDate"
                    label="Event End Date"
                    placeholder="End Date"
                    isRequired
                    dateOnly={true}
                  />
                </WrapItem>
              </Wrap>

              <Wrap w="100%" spacing={5}>
                <WrapItem flex="1" maxWidth="180px">
                  <TimeField
                    name="checkInStartTime"
                    label="Check-in Start Time"
                    placeholder="Start Time"
                    isRequired
                  />
                </WrapItem>
                <WrapItem flex="1" maxWidth="180px">
                  <TimeField
                    name="checkInEndTime"
                    label="Check-in End Time"
                    placeholder="End Time"
                    isRequired
                  />
                </WrapItem>
              </Wrap>

              <TextField
                name="eventLocation"
                label="Event Location"
                placeholder="Event Location"
                isRequired
              />

              <ChakraReactSelectField
                label="Event Timezone"
                name="eventTimezone"
                selectedOptionColor="blue"
                isLoading={isLoading}
                isRequired
                options={
                  timezones?.map((timezone) => {
                    return {
                      label: timezone.Value!,
                      value: timezone.Key!,
                    };
                  }) || []
                }
                className="relative-listbox"
                onChange={(e) => {
                  const label = (e as SingleValue<SelectOption>)?.label;
                  setFieldValue("eventTimezone", label);
                }}
              />

              <FieldWrapper
                name={"memberFilterQuery"}
                id={"memberFilterQuery"}
                label={"Active Member Filter Query"}
                horizontal={true}
              >
                {({ field }) => (
                  <HStack w="100%">
                    <Box w="100%" bgColor="white">
                      <Textarea
                        {...field}
                        isReadOnly
                        valueProps={{ flex: 2 }}
                        value={filterQuery ?? ""}
                        fontSize="xs"
                      />
                    </Box>
                    <Box flexShrink={1}>
                      <Button
                        variant="solid"
                        color="#fff"
                        onClick={memberFilterQueryModal.onOpen}
                        alignSelf="flex-end"
                        colorScheme="cherryButton"
                      >
                        Edit Filter
                      </Button>
                    </Box>
                  </HStack>
                )}
              </FieldWrapper>
            </VStack>
          </Box>

          {/* Right side - Preview */}
          <Box w={["100%", "50%"]}>
            <EventPreview values={values} memberCount={memberCount} />
          </Box>
        </Stack>
      </VStack>
      <MemberFilterQueryModal
        size="4xl"
        {...memberFilterQueryModal}
        businessId={businessId}
        memberQueryFilter={filterQuery ?? ""}
        onSuccess={onSuccessMemberFilterQuery}
      />
    </Card>
  );
};

export const EventForm = withFormik<EventFormProps, EventFormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ event }) => {
    return {
      eventKey: event?.id,
      eventName: event?.EventName ?? "",
      eventStartDate: event?.EventStartDate ?? "",
      eventEndDate: event?.EventEndDate ?? "",
      checkInStartTime: event?.CheckInStartTime ?? "",
      checkInEndTime: event?.CheckInEndTime ?? "",
      eventLocation: event?.EventLocation ?? "",
      eventTimezone: event?.TimeZoneId ?? null,
      memberFilterQuery: event?.MemberFilterQuery ?? "",
      isPublishRequested: false,
    };
  },

  handleSubmit: async (values, { props, setFieldError }) => {
    let isInvalid = false;

    if (isEmptyStr(values.eventName)) {
      setFieldError("eventName", "Event Name is required");
      isInvalid = true;
    }

    if (isEmptyStr(values.eventStartDate)) {
      setFieldError("eventStartDate", "Event Start Date is required");
      isInvalid = true;
    }

    if (isEmptyStr(values.eventEndDate)) {
      setFieldError("eventEndDate", "Event End Date is required");
      isInvalid = true;
    }

    if (isEmptyStr(values.checkInStartTime)) {
      setFieldError("checkInStartTime", "Check-in Start Time is required");
      isInvalid = true;
    }

    if (isEmptyStr(values.checkInEndTime)) {
      setFieldError("checkInEndTime", "Check-in End Time is required");
      isInvalid = true;
    }

    if (isEmptyStr(values.eventTimezone)) {
      setFieldError("eventTimezone", "Event Timezone is required");
      isInvalid = true;
    }

    if (isEmptyStr(values.eventLocation)) {
      setFieldError("eventLocation", "Event Location is required");
      isInvalid = true;
    }

    if (isInvalid) {
      return;
    }

    const eventUpdate: Partial<CherryPayApi.EventUpdate> = {
      EventName: values.eventName,
      EventStartDate: values.eventStartDate,
      EventEndDate: values.eventEndDate,
      CheckInStartTime: values.checkInStartTime,
      CheckInEndTime: values.checkInEndTime,
      EventLocation: values.eventLocation,
      TimeZoneId: values.eventTimezone,
      MemberFilterQuery: values.memberFilterQuery,
    };

    let result: ApiResult<CherryPayApi.Event> | null = null;
    const isPublishing = values.isPublishRequested;

    if (props.event == null) {
      result = await props.apiClient.createEvent(eventUpdate, props.businessId);
    } else {
      result = await props.apiClient.updateEvent(
        eventUpdate,
        props.businessId,
        props.event.id
      );
    }

    if (result.ok) {
      // If publish was clicked, call setEventStatus after successful create/update
      if (isPublishing) {
        const eventId = result.data.id;
        const publishResult = await props.apiClient.setEventStatus(
          props.businessId,
          eventId,
          "publish"
        );

        if (publishResult.ok) {
          // Use the most recently returned event data for onSuccess
          if (props.onSuccess) {
            props.onSuccess(publishResult.data);
          }
        } else {
          if (props.onFailure) {
            props.onFailure(
              `Event created but publishing failed: ${publishResult.message}`
            );
          }
        }
      } else {
        // Normal save completion without publishing
        if (props.onSuccess) {
          props.onSuccess(result.data);
        }
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnMount: false,
  validateOnBlur: true,

  validate: (values) => {
    const errors: FormikErrors<EventFormValues> = {};

    // Add validation for start date being before end date
    if (values.eventStartDate && values.eventEndDate) {
      const startDate = new Date(values.eventStartDate);
      const endDate = new Date(values.eventEndDate);

      if (startDate > endDate) {
        errors.eventEndDate = "End date must be after start date";
      }
    }

    // Add validation for check-in times
    if (values.checkInStartTime && values.checkInEndTime) {
      const startTime = new Date(`1970-01-01T${values.checkInStartTime}`);
      const endTime = new Date(`1970-01-01T${values.checkInEndTime}`);

      if (startTime > endTime) {
        errors.checkInEndTime = "Check-in end time must be after start time";
      }
    }

    return errors;
  },
})(InnerForm);
